import { collection, doc, getDoc, query, setDoc, where } from 'firebase/firestore';
import { db } from 'shared/services/firebase';
import { getQueryOnce } from 'shared/services/firestore';
import { mapManager, Manager } from 'domains/customers/model/manager';
import { createCustomer } from 'domains/customers/data/customers';
import { UserAccount } from 'shared/types/userAccount';

/**
 * `managers` collection — ported from aligned's `ManagementService` +
 * `FirestoreApiService` (`management_service.dart`, `firestore_api_service.dart:2058-2087,1975-2008`).
 * A single user can have more than one `Manager` doc (e.g. after
 * onboarding's "Connect Company" step creates a second one) — there is no
 * `deprecatedOn` field on this collection.
 */

const MANAGERS = 'managers';
const USERS = 'users';

/** Every manager account attached to a user account, preserving Firestore's
 * query order like aligned's `getManagerListByUserAccountId`. */
export function getManagerListByUserAccountId(userAccountId: string): Promise<Manager[]> {
  return getQueryOnce(
    query(collection(db, MANAGERS), where('userAccountId', '==', userAccountId)),
    mapManager,
  );
}

/**
 * The manager doc for this user account, preferring the one whose
 * `customerId` matches `currentCustomerId` and falling back to the first
 * doc in the query snapshot if none match (or if `currentCustomerId` is
 * absent) — matches `getManagerByUserAccountId` exactly, including that it
 * does NOT filter by any deprecation field.
 */
export async function getManagerByUserAccountId(
  userAccountId: string,
  currentCustomerId: string | null,
): Promise<Manager | null> {
  const rows = await getManagerListByUserAccountId(userAccountId);
  if (rows.length === 0) return null;
  const match = currentCustomerId ? rows.find((m) => m.customerId === currentCustomerId) : undefined;
  return match ?? rows[0];
}

/** Customer detail/admin read: every manager doc attached to this customer, with no deprecation filter. */
export function getManagersForCustomer(customerId: string): Promise<Manager[]> {
  return getQueryOnce(
    query(collection(db, MANAGERS), where('customerId', '==', customerId)),
    mapManager,
  );
}

export interface NewManagerInput {
  firebaseId: string;
  userAccountId: string;
  userAccountEmail: string | null;
  customerId: string;
  isSelfServe?: boolean;
  isFirstTime?: boolean;
}

/**
 * Creates a `managers` doc, then appends its id (and its customer's id) onto
 * `users/{firebaseId}`'s `managerIds`/`customerIds` arrays — matches
 * `firestore_api_service.dart:1975-2008`'s create-then-append-to-user-doc
 * sequence. The append is read-modify-write against whatever's already on
 * that doc, never clobbering existing ids (same append-only pattern as
 * `onboarding_account`'s `previousCountries`).
 */
export async function createManager(input: NewManagerInput): Promise<Manager> {
  const ref = doc(collection(db, MANAGERS));
  const data = {
    id: ref.id,
    firebaseId: input.firebaseId,
    userAccountId: input.userAccountId,
    userAccountEmail: input.userAccountEmail,
    customerId: input.customerId,
    isSelfServe: input.isSelfServe ?? false,
    isFirstTime: input.isFirstTime ?? false,
  };
  await setDoc(ref, data);

  const usersRef = doc(db, USERS, input.firebaseId);
  const usersSnap = await getDoc(usersRef);
  const existing = usersSnap.exists() ? (usersSnap.data() as Record<string, unknown>) : {};
  const managerIds = [...((existing.managerIds as string[] | undefined) ?? [])];
  if (!managerIds.includes(ref.id)) managerIds.push(ref.id);
  const customerIds = [...((existing.customerIds as string[] | undefined) ?? [])];
  if (!customerIds.includes(input.customerId)) customerIds.push(input.customerId);
  await setDoc(usersRef, { managerIds, customerIds }, { merge: true });

  return mapManager(ref.id, data);
}

/**
 * Full first-time setup for a brand-new manager, matching
 * `createManagerAccountSetup` (`management_service.dart:311-339`): a new
 * `Customer` named after the user's email, then a `Manager` doc pointed at
 * it (`isSelfServe: false, isFirstTime: true` — matches upstream exactly;
 * "self-serve" here refers to a different, unused signup path).
 */
export async function createManagerAccountSetup(userAccount: UserAccount): Promise<Manager> {
  const customer = await createCustomer({
    name: userAccount.email,
    contactEmail: userAccount.email,
    contactName: userAccount.firstName ?? null,
  });
  return createManager({
    firebaseId: userAccount.firebaseId,
    userAccountId: userAccount.id,
    userAccountEmail: userAccount.email,
    customerId: customer.id,
    isSelfServe: false,
    isFirstTime: true,
  });
}

/** Plain merge-update, matches `ManagementService.updateManager` (`management_service.dart:304-307`). */
export function updateManager(managerId: string, fields: Partial<Manager>): Promise<void> {
  return setDoc(doc(db, MANAGERS, managerId), fields, { merge: true });
}
